/**
 * Showroom QR sheets — the pure half: which link a card's code opens, how
 * cards sit on an A4 page (in millimetres, shared by the on-screen preview,
 * the print view and the PDF so all three match), how a price reads, and the
 * QR code as a grid of modules.
 */
import { buildVisualizerUrl } from '../../../../common/utils/visualizer-link';
import type { ProductPrice } from '../types';

// ── The code's link ─────────────────────────────────────────────────────

/**
 * The visualizer, opened on the product, stamped so the visit reads as
 * "showroom QR" in analytics and on any request it leads to: utm_source
 * showroom, utm_medium qr, the store as the campaign — plus `src=showroom`
 * and `store=<id>` for the visualizer itself (nearest store = this one).
 */
export function showroomQrUrl({ alias, sku, storeId }: { alias: string; sku?: string; storeId?: string | null }): string {
    const base = buildVisualizerUrl({
        alias,
        sku,
        attribution: { source: 'showroom', medium: 'qr', campaign: storeId || 'showroom' },
    });
    if (!base) return '';
    const url = new URL(base, 'https://placeholder.invalid');
    url.searchParams.set('src', 'showroom');
    if (storeId) url.searchParams.set('store', storeId);
    // A relative base (no visualizer origin configured) stays relative.
    return base.startsWith('http') ? url.toString() : `${url.pathname}${url.search}`;
}

// ── A4 layout ───────────────────────────────────────────────────────────

export const A4 = { width: 210, height: 297 } as const;

export type SheetLayoutKey = 'large' | 'compact';

export interface SheetLayout {
    key: SheetLayoutKey;
    cols: number;
    rows: number;
    /** Page margin, mm — clear of most printers' unprintable edge. */
    margin: number;
    /** Space between cards, mm — room to cut between them. */
    gutter: number;
    /** Inside a card, mm. */
    padding: number;
    /** Type sizes, pt. */
    type: { name: number; price: number; cta: number; store: number };
}

/** Two choices, kept simple: 6 large cards a page (2 × 3) or 12 compact ones (3 × 4). */
export const SHEET_LAYOUTS: Record<SheetLayoutKey, SheetLayout> = {
    large: { key: 'large', cols: 2, rows: 3, margin: 10, gutter: 6, padding: 5, type: { name: 12, price: 11, cta: 8.5, store: 7.5 } },
    compact: { key: 'compact', cols: 3, rows: 4, margin: 10, gutter: 5, padding: 3.5, type: { name: 9, price: 8.5, cta: 6.5, store: 6 } },
};

export interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface CardBoxes {
    /** The card's outline (the cut line). */
    frame: Rect;
    photo: Rect;
    qr: Rect;
    /** Name, price, call to action, store — top to bottom. */
    text: Rect;
}

export const cardsPerPage = (layout: SheetLayout): number => layout.cols * layout.rows;

/** Size of one card on the page, mm. */
export function cardSize(layout: SheetLayout): { w: number; h: number } {
    const w = (A4.width - 2 * layout.margin - (layout.cols - 1) * layout.gutter) / layout.cols;
    const h = (A4.height - 2 * layout.margin - (layout.rows - 1) * layout.gutter) / layout.rows;
    return { w, h };
}

/** Where the parts of a card go inside its frame: the photo and the code side by side on top, the words under them. */
export function cardBoxes(frame: Rect, layout: SheetLayout): CardBoxes {
    const p = layout.padding;
    const innerW = frame.w - 2 * p;
    const innerH = frame.h - 2 * p;
    const gap = p;
    const square = Math.min((innerW - gap) / 2, innerH * 0.56);
    const photo = { x: frame.x + p, y: frame.y + p, w: square, h: square };
    const qr = { x: frame.x + frame.w - p - square, y: frame.y + p, w: square, h: square };
    const textTop = frame.y + p + square + gap * 0.8;
    const text = { x: frame.x + p, y: textTop, w: innerW, h: frame.y + frame.h - p - textTop };
    return { frame, photo, qr, text };
}

/** Every card's boxes, page by page, for `count` products. */
export function sheetPages(count: number, layout: SheetLayout): CardBoxes[][] {
    const per = cardsPerPage(layout);
    const { w, h } = cardSize(layout);
    const pages: CardBoxes[][] = [];
    for (let i = 0; i < count; i++) {
        const slot = i % per;
        if (slot === 0) pages.push([]);
        const col = slot % layout.cols;
        const row = Math.floor(slot / layout.cols);
        const frame = { x: layout.margin + col * (w + layout.gutter), y: layout.margin + row * (h + layout.gutter), w, h };
        pages[pages.length - 1].push(cardBoxes(frame, layout));
    }
    return pages;
}

// ── Price ───────────────────────────────────────────────────────────────

/** A price worth printing: an amount and a currency, or nothing — never a guessed one. */
export function readPrice(raw: any): ProductPrice | null {
    const amount = Number(raw?.amount);
    const currency = String(raw?.currency || '').trim().toUpperCase();
    if (raw?.amount === null || raw?.amount === undefined || raw?.amount === '' || !Number.isFinite(amount) || amount <= 0 || !/^[A-Z]{3}$/.test(currency)) {
        return null;
    }
    const units: ProductPrice['unit'][] = ['m2', 'box', 'piece', 'unit', 'linear_m', 'roll'];
    const unit = units.includes(raw?.unit) ? raw.unit : 'unit';
    return { amount, currency, unit };
}

/** "€42.90 / m²" — the unit label comes translated from the caller. */
export function formatPriceWithUnit(price: ProductPrice, lang: string, unitLabel: string): string {
    const locale = ['en', 'fr', 'es', 'ar'].includes(lang) ? lang : 'en';
    let money: string;
    try {
        money = new Intl.NumberFormat(locale, { style: 'currency', currency: price.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price.amount);
    } catch {
        money = `${price.amount.toFixed(2)} ${price.currency}`;
    }
    return unitLabel ? `${money} / ${unitLabel}` : money;
}

/**
 * Text jsPDF's built-in Helvetica can draw. Its encoding (WinAnsi) has no
 * narrow no-break space — which Intl puts in French prices — so spaces are
 * made plain; anything else outside Latin-1 + € is dropped rather than printed
 * as garbage.
 */
export function pdfSafeText(text: string): string {
    return String(text || '')
        .replace(/[\u00A0\u202F\u2007\u2009]/g, ' ')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/\u2026/g, '...')
        // eslint-disable-next-line no-control-regex
        .replace(/[^\u0000-\u00FF\u20AC]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

// ── QR code ─────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-var-requires
const qrcode: any = require('../../../../vendor/qrcode-generator/qrcode.js');

/** The code as rows of dark (true) / light modules, error correction M. Empty for an empty text. */
export function qrMatrix(text: string): boolean[][] {
    if (!text) return [];
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n: number = qr.getModuleCount();
    const rows: boolean[][] = [];
    for (let r = 0; r < n; r++) {
        const row: boolean[] = [];
        for (let c = 0; c < n; c++) row.push(!!qr.isDark(r, c));
        rows.push(row);
    }
    return rows;
}

/**
 * Dark modules merged into horizontal runs — one rectangle per run instead of
 * one per module, for the PDF and the SVG path alike.
 */
export function qrRuns(matrix: boolean[][]): Array<{ row: number; col: number; len: number }> {
    const runs: Array<{ row: number; col: number; len: number }> = [];
    matrix.forEach((cells, row) => {
        let start = -1;
        for (let col = 0; col <= cells.length; col++) {
            const dark = col < cells.length && cells[col];
            if (dark && start < 0) start = col;
            if (!dark && start >= 0) {
                runs.push({ row, col: start, len: col - start });
                start = -1;
            }
        }
    });
    return runs;
}

/** Quiet zone around the code, in modules (the spec asks for 4; 2 is plenty on a white card). */
export const QR_QUIET = 2;

/** An SVG path of the code's dark runs, in module units, offset by the quiet zone. */
export function qrPath(matrix: boolean[][], quiet = QR_QUIET): string {
    return qrRuns(matrix)
        .map((r) => `M${r.col + quiet} ${r.row + quiet}h${r.len}v1h-${r.len}z`)
        .join('');
}

/** Type size in points → millimetres. */
export const ptToMm = (pt: number): number => (pt * 25.4) / 72;

/** Card text cut to `maxLines` lines, the last one ending in an ellipsis when something was cut. */
export function clampLines(lines: string[], maxLines: number): string[] {
    if (lines.length <= maxLines) return lines;
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].replace(/[\s.,;:-]+$/, '')}...`;
    return kept;
}

// ── Products ────────────────────────────────────────────────────────────

export interface ShowroomProduct {
    uuid: string;
    sku: string;
    name: string;
    imageUrl: string | null;
    price: ProductPrice | null;
}

const str = (v: any): string => {
    if (v == null) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number') return String(v);
    if (typeof v === 'object' && 'value' in v) return str(v.value);
    return '';
};

/** A product from the V2 list as a card needs it. Null without a uuid. */
export function toShowroomProduct(raw: any): ShowroomProduct | null {
    const uuid = str(raw?.uuid);
    if (!uuid) return null;
    const gallery = Array.isArray(raw?.galleryImages) ? raw.galleryImages.map((g: any) => (typeof g === 'string' ? g : g?.url || g?.path)).find(Boolean) : null;
    const imageUrl = raw?.visualization?.thumbnailPath || gallery || raw?.visualization?.surface?.materialPath || null;
    return {
        uuid,
        sku: str(raw?.sku),
        name: str(raw?.name) || str(raw?.sku) || uuid,
        imageUrl: imageUrl && !String(imageUrl).endsWith('/images/default-thumb.jpg') ? String(imageUrl) : null,
        price: readPrice(raw?.commerce?.price),
    };
}
